import type { AppConfig, FeatureConfig } from "./types"

/** Built-in defaults: the first of the four configuration layers. */
export const DEFAULT_CONFIG: AppConfig = {
  version: 1,
  branding: {
    name: "Jumble",
    shortName: "Jumble",
    description: "A user-friendly Nostr client for exploring relay feeds.",
    logo: "/logo.svg",
    favicon: "/favicon.svg",
    titleTemplate: "{page} · {name}",
    footerText: "",
    links: [],
  },
  design: {
    colors: {
      primary: "#7c62c8",
      accent: "#4f9cf9",
    },
    theme: {
      default: "dark",
      allowed: ["light", "dark", "pure-black"],
    },
    radius: 12,
    density: "comfortable",
    fontScale: 1,
    fontFamily:
      "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
  },
  features: {
    explore: true,
    relayReviews: true,
    notifications: true,
    bookmarks: true,
    search: true,
    composer: true,
    relaySets: true,
    trending: true,
    imageUploads: false,
  },
  relays: {
    defaultRelays: [
      "wss://nos.lol/",
      "wss://relay.primal.net/",
      "wss://offchain.pub/",
      "wss://relay.ditto.pub/",
    ],
    searchRelays: ["wss://search.nos.today/", "wss://search.nostrarchives.com/"],
    trendingRelays: ["wss://trending.relays.land/"],
    discoveryRelays: ["wss://nos.lol/", "wss://relay.primal.net/", "wss://offchain.pub/"],
    discoveryCollectionsUrl:
      "https://raw.githubusercontent.com/CodyTseng/awesome-nostr-relays/master/dist/collections.json",
    relayOptions: {
      connectTimeout: 7000,
      publishTimeout: 9000,
      maxReconnectAttempts: 6,
      reconnectBaseDelay: 1200,
      verifyEvents: true,
      eoseTimeout: 4500,
      relayInfoTimeout: 6000,
      infoRefreshMs: 6 * 60 * 60 * 1000,
    },
  },
  navigation: {
    items: [
      { id: "home", icon: "home", path: "/", visible: true },
      { id: "explore", icon: "compass", path: "/explore", visible: true },
      { id: "notifications", icon: "bell", path: "/notifications", visible: true },
      { id: "search", icon: "search", path: "/search", visible: true },
      { id: "profile", icon: "user", path: "/profile", visible: true },
      { id: "bookmarks", icon: "bookmark", path: "/bookmarks", visible: true },
      { id: "settings", icon: "settings", path: "/settings", visible: true },
    ],
  },
  i18n: {
    defaultLanguage: "en",
    languages: ["en", "zh"],
  },
  auth: {
    allowSignup: true,
    // Empty by default: Google sign-in is opt-in for deployments that own a
    // Google OAuth client ID (see docs/CONFIGURATION.md).
    googleClientId: "",
    nip46Relays: ["wss://relay.nsec.app/", "wss://relay.damus.io/"],
    nip46Permissions: [
      "get_public_key",
      "sign_event:0",
      "sign_event:1",
      "sign_event:3",
      "sign_event:5",
      "sign_event:6",
      "sign_event:7",
      "sign_event:10000",
      "sign_event:10002",
      "sign_event:10003",
      "sign_event:10012",
      "sign_event:22242",
      "sign_event:30002",
      "sign_event:30023",
      "sign_event:31987",
      "nip04_encrypt",
      "nip04_decrypt",
    ],
  },
  community: {
    mode: false,
    relaySets: [],
    extraRelays: [],
  },
}

export const FEATURE_KEYS: Array<keyof FeatureConfig> = [
  "explore",
  "relayReviews",
  "notifications",
  "bookmarks",
  "search",
  "composer",
  "relaySets",
  "trending",
  "imageUploads",
]

/** Clone that is safe for the nested plain data in the config. */
export function cloneConfig<T>(value: T): T {
  return structuredClone(value)
}
